import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

const shell = (title, sub, children) => (
  <div className="min-h-screen flex items-center justify-center p-6 bg-tc-bg" data-testid="auth-shell">
    <div className="w-full max-w-md tc-card p-8">
      <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach">TRADECRAFT</div>
      <h1 className="font-heading text-2xl font-extrabold mt-1">{title}</h1>
      {sub && <p className="text-tc-sub text-sm mt-1">{sub}</p>}
      <div className="mt-5 space-y-3">{children}</div>
    </div>
  </div>
);
const input = "w-full bg-tc-bg border border-white/15 rounded-lg px-3 py-2.5 text-tc-txt";
const btn = "w-full rounded-lg bg-tc-coach text-tc-bg font-semibold py-2.5 disabled:opacity-60";

export function Signup() {
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", is_minor: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    api.post("/commerce/telemetry", { event: "signup_started" }).catch(() => {});
    try {
      const { data } = await api.post("/auth/register", { name: f.name, email: f.email, password: f.password, is_minor: f.is_minor });
      localStorage.setItem("tc_token", data.token);
      api.post("/commerce/telemetry", { event: "signup_completed" }).catch(() => {});
      toast.success("Welcome to TRADECRAFT");
      window.location.href = "/dashboard";
    } catch (err) { toast.error(apiErr(err)); setBusy(false); }
  };
  return shell("Create your account", "Start free — learn how professionals reason.", (
    <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
      <input data-testid="signup-name" className={input} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
      <input data-testid="signup-email" type="email" className={input} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
      <input data-testid="signup-password" type="password" className={input} placeholder="Password (min 8)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required minLength={8} />
      <div className="flex gap-2 text-sm">
        {[[false, "I'm an adult (18+)"], [true, "Learner / minor"]].map(([v, l]) => (
          <button type="button" key={String(v)} data-testid={`signup-agegroup-${v ? "minor" : "adult"}`} onClick={() => setF({ ...f, is_minor: v })} className={`flex-1 rounded-lg border py-2 ${f.is_minor === v ? "border-tc-coach text-tc-coach" : "border-white/15 text-tc-sub"}`}>{l}</button>
        ))}
      </div>
      <button data-testid="signup-submit" className={btn} disabled={busy}>{busy ? "Creating…" : "Create account"}</button>
      <p className="text-tc-muted text-xs text-center">By continuing you agree to our <Link to="/legal" className="text-tc-coach">Terms & Privacy</Link>. Educational tool — no financial guarantees.</p>
      <p className="text-tc-sub text-sm text-center">Have an account? <Link to="/login" className="text-tc-coach">Sign in</Link></p>
    </form>
  ));
}

export function ForgotPassword() {
  const [email, setEmail] = useState(""); const [sent, setSent] = useState(false);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { toast.error(apiErr(err)); } };
  return shell("Reset your password", "We'll email you a reset link.", sent ? (
    <div data-testid="forgot-sent" className="text-tc-sub text-sm">If that email exists, a reset link has been sent. <Link to="/login" className="text-tc-coach">Back to sign in</Link></div>
  ) : (
    <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
      <input data-testid="forgot-email" type="email" className={input} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      <button data-testid="forgot-submit" className={btn}>Send reset link</button>
      <p className="text-tc-sub text-sm text-center"><Link to="/login" className="text-tc-coach">Back to sign in</Link></p>
    </form>
  ));
}

export function ResetPassword() {
  const [params] = useSearchParams(); const navigate = useNavigate();
  const [pw, setPw] = useState(""); const token = params.get("token");
  const submit = async (e) => { e.preventDefault(); try { await api.post("/auth/reset-password", { token, password: pw }); toast.success("Password updated — sign in"); navigate("/login"); } catch (err) { toast.error(apiErr(err)); } };
  return shell("Choose a new password", null, (
    <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
      <input data-testid="reset-password" type="password" className={input} placeholder="New password (min 8)" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} />
      <button data-testid="reset-submit" className={btn}>Update password</button>
    </form>
  ));
}
